<template>
    <div class="o-d-map d-map" :class="{'d-map--print': print}">
        <resize-observer @notify="handleResize" />
        <transition name="loader-fade">
            <ALoader v-if="showLoader"/>
        </transition>
        <div
            v-if="print"
            class="d-map__placeholder"
        />
        <ODMapController/>
        <div class="d-map__wrapper">
            <ASwitchCircle
                v-model="contrast"
                :label="$t('ui.switch-contrast')"
            />
            <ACompassButton
                class="ui ui-map-buttons"
                v-model="gyroScopeBtnState"
                :rotate="compassDegree"
            />
            <MZoomControls />
            <ACompanyInfo />
        </div>
        <AButton
            v-if="isStartButton"
            class="d-map__start-button"
            :text="$t('ui.start-directions')"
            @onClick="handleClick"
        >
            <template #right>
                <span class="d-map__start-button-wrapper">
                    <svg-icon
                        original
                        name="icons/arrow-right"
                        class="d-map__start-button-icon"
                    />
                </span>
            </template>
        </AButton>
        <OStepsPanel
            v-if="isNavPanel"
            @onClose="handleClose"
        />
        <OResetMapView
            v-if="isShowResetMapView && isGuideScreen"
            @closeResetMapView="isShowResetMapView = false"
        />
    </div>
</template>

<script>
import {
    mapState,
    mapGetters,
    mapActions
} from "vuex";

import AButton from '../../atoms/a-button/a-button';
import ACompanyInfo from '../../atoms/a-company-info/a-company-info';
import OStepsPanel from '../../organisms/o-steps-panel/o-steps-panel';
import ASwitchCircle from '../../atoms/a-switch-circle/a-switch-circle';
import ACompassButton from '../../atoms/a-compass-button/a-compass-button';
import MZoomControls from '../../molecules/m-zoom-controls/m-zoom-controls';
import OResetMapView from "../../organisms/o-reset-map-view/o-reset-map-view";
import ODMapController from "../../organisms/o-d-map-controller/o-d-map-controller";

import {
    UI_MAP_READY,
    UI_MAP_CLICKED,
    UI_ON_CAMERA_UPDATE
} from "../../../shared/components/map-viewer/constants/map-events";
import { WEB_GUIDE } from '../../../utils/constants/pages';
import { START_GUIDE_OVERVIEW } from "../../../utils/constants/bus-events-constants";
import ALoader from "../../atoms/a-loader/a-loader";

import {
    SET_GYROSCOPE_STATE,
    TURN_ON_DEVICE_ORIENTATION_EVENTS,
    DISABLE_TOUCH_HORIZONTAL_ROTATION
} from '../../../shared/components/map-viewer/constants/map-commands';

export default {
    name: 'ODMap',
    components: {
        ALoader,
        AButton,
        OStepsPanel,
        MZoomControls,
        ACompanyInfo,
        ASwitchCircle,
        ACompassButton,
        ODMapController,
        OResetMapView
    },
    data() {
        return {
            print: false,
            compassDegree: 0,
            screenState: 'start',
            contrast: false,
            isShowResetMapView: false,
            showLoader: true,
            gyroScopeBtnState: true
        }
    },
    computed: {
        ...mapState({
            activeStepIndex: state => state.activeStepIndex,
            mapLoading: state => state.mapLoading
        }),
        ...mapGetters([
            'getMapConfigProp'
        ]),
        compassCorrectionValue() {
            return this.getMapConfigProp('compass_correction', 0);
        },
        isGuideScreen() {
            return this.$route.name === WEB_GUIDE;
        },
        isStartButton() {
            return this.isGuideScreen && this.screenState === 'start' && this.$route.query.to && this.getMapConfigProp('step_by_step_panel');
        },
        isNavPanel() {
            return this.isGuideScreen && this.screenState === 'nav' && this.getMapConfigProp('step_by_step_panel');
        }
    },
    mounted() {
        this.initEventListeners();
    },
    beforeDestroy() {
        this.deactivateEventsListeners();
    },
    watch: {
        '$route'() {
            this.isShowResetMapView = false;
            this.screenState = 'start';
        },
        activeStepIndex(value) {
            if (value !== null) this.screenState = 'nav';
            this.isShowResetMapView = false;
        },
        contrast(val){
            const themeName = val ? 'terminal-contrast' : 'terminal'
            this.$store.commit('setActiveMapTheme', themeName);
        },
        mapLoading(){
            setTimeout(() => this.showLoader = false, 500)
        },
        gyroScopeBtnState: {
            handler(value) {
                if (value) this.modelOrientation();
                else this.compassOrientation();
            },
            immediate: true
        }
    },
    methods: {
        changePrintMode() {
            this.print = !this.print;
        },
        ...mapActions([
            'saveActivePathToState'
        ]),
        initEventListeners() {
            this.$bus.$on(UI_MAP_READY, this.initMapEventListeners);
            this.$bus.$on(START_GUIDE_OVERVIEW, this.changeToStart);
            this.$bus.$on(UI_ON_CAMERA_UPDATE, this.updateOrientation);
            this.$bus.$on(UI_MAP_CLICKED, this.onMapClicked);
            window.addEventListener("beforeprint", this.changePrintMode);
            window.addEventListener("afterprint", this.changePrintMode);
        },
        deactivateEventsListeners() {
            this.deactivateMapEventListeners();
            this.$bus.$off(START_GUIDE_OVERVIEW, this.changeToStart);
            this.$bus.$off(UI_ON_CAMERA_UPDATE, this.updateOrientation);
            this.$bus.$off(UI_MAP_CLICKED, this.onMapClicked);
            window.removeEventListener("beforeprint", this.changePrintMode);
            window.removeEventListener("afterprint", this.changePrintMode);
        },
        initMapEventListeners(){
            document.viewApi.register_event('on_camera_update', this.updateOrientation);
        },
        deactivateMapEventListeners(){
            document.viewApi.deregister_event('on_camera_update', this.updateOrientation);
        },
        updateOrientation() {
            // Here we need to provide some performance efficient access to viewApi props and methods
            // Using bus/vuex looks not effective here because of high call rate ~60 times a second
            if(document.viewApi)
                this.compassDegree = this.compassCorrectionValue +  document.viewApi.camera_controller.current_orientation
        },
        handleClick() {
            this.screenState = 'nav';
        },
        changeToStart() {
            this.screenState = 'start';
        },
        handleClose() {
            this.screenState = 'start';
            this.$bus.$emit(START_GUIDE_OVERVIEW, true);
        },
        onMapClicked() {
            if(this.isGuideScreen) this.isShowResetMapView = true;
        },
        handleResize() {
            this.$bus.$emit(UI_ON_CAMERA_UPDATE);
        },
        modelOrientation() {
            this.initMapEventListeners();
            window.removeEventListener('deviceorientation', this.addDeviceOrientationListener);
            this.$bus.$on(UI_ON_CAMERA_UPDATE, this.updateOrientation);
            this.$bus.$emit(DISABLE_TOUCH_HORIZONTAL_ROTATION, false);
            this.$bus.$emit(SET_GYROSCOPE_STATE, false);
        },
        compassOrientation() {
            this.$bus.$off(UI_ON_CAMERA_UPDATE, this.updateOrientation);
            this.$bus.$emit(SET_GYROSCOPE_STATE, true);
            this.$bus.$emit(DISABLE_TOUCH_HORIZONTAL_ROTATION, true);
            this.$bus.$emit(TURN_ON_DEVICE_ORIENTATION_EVENTS, true);

            // TODO: refactoring

            const IOS = navigator.platform.indexOf('iPhone') !== -1;

            if (IOS) {
                window.DeviceOrientationEvent.requestPermission()
                    .then(response => {
                        if (response === 'granted') {
                            window.addEventListener('deviceorientation', this.addDeviceOrientationListener);
                        }
                    })
                    .catch(e => {
                        this.$bus.$emit(NOTIFY_ERROR, e);
                    })
            } else {
                window.addEventListener('deviceorientation', this.addDeviceOrientationListener);
            }
        },
        addDeviceOrientationListener(event) {
            const IOS = navigator.platform.indexOf('iPhone') !== -1;
            if (IOS) {
                this.compassDegree = 360 - Math.round(event.webkitCompassHeading);
            } else {
                this.compassDegree = Math.round(event.alpha);
            }
        }
    }
}
</script>

<style lang="scss">
@import "o-d-map";
</style>
